import { DATE_FORMATS, DEFAULT_DATE_FORMAT } from "./bookingLink";
import {
  EMAIL_RE,
  MAX_CUSTOM_FIELDS,
  MAX_FIELD_LABEL,
  RESERVATION_FIELDS,
  ReservationFieldSetting,
  ReservationMode,
  slugify,
  templatePlaceholders,
} from "./reservationTypes";

/** The built-in details the chatbot can ask a guest who wants to cancel. */
export const CANCEL_REQUEST_FIELDS = ["booking_id", "name", "email", "phone", "date"] as const;
export type CancelRequestField = (typeof CANCEL_REQUEST_FIELDS)[number];
export const DEFAULT_CANCEL_REQUEST_FIELDS: CancelRequestField[] = ["booking_id", "email"];
const CONTACT_FIELDS: string[] = ["email", "phone"];
/** Ids the chatbot keeps for itself or drops, so a custom detail can't use them. */
const TAKEN_IDS = [...RESERVATION_FIELDS, "booking_id", "hotel_id", "session_id", "conversation_id", "reservation_type"];
const SLUG_RE = /^[a-z0-9]+(?:[-_][a-z0-9]+)*$/;
const MAX_URL_TEMPLATE = 2000;
const MAX_EMAIL = 254;

export const CANCEL_REQUEST_FIELD_LABELS: Record<CancelRequestField, string> = {
  booking_id: "Booking ID",
  name: "Name",
  email: "Email",
  phone: "Phone",
  date: "Check-in date",
};

export const CANCEL_REQUEST_MODE_OPTIONS: { value: ReservationMode; label: string }[] = [
  { value: "link_out", label: "Ask for details, then open our cancel page" },
  { value: "enquiry", label: "Ask for details, then email us" },
];

/** ``content.cancelRequest`` of the Cancel Booking record, as the editor holds it. */
export interface CancelRequest {
  enabled: boolean;
  mode: ReservationMode;
  urlTemplate: string;
  enquiryEmail: string;
  dateFormat: string;
  fields: ReservationFieldSetting[];
}

export type CancelRequestErrors = Partial<Record<"cancelRequestUrl" | "cancelRequestEmail" | "cancelRequestFields", string>>;

export const isCancelRequestField = (value: unknown): value is CancelRequestField =>
  (CANCEL_REQUEST_FIELDS as readonly string[]).includes(value as string);

export const cancelRequestCustomFields = (setting: CancelRequest) => setting.fields.filter((field) => field.custom);

export function cancelFieldLabel(field: ReservationFieldSetting): string {
  if (!field.custom && isCancelRequestField(field.id)) return CANCEL_REQUEST_FIELD_LABELS[field.id];
  return (field.label || "").trim() || field.id;
}

/** A new setting: link to the existing cancel page when there is one, else email the hotel. */
export function newCancelRequest(cancelUrl = "", enquiryEmail = ""): CancelRequest {
  const url = cancelUrl.trim();
  return {
    enabled: true,
    mode: url ? "link_out" : "enquiry",
    urlTemplate: url,
    enquiryEmail: enquiryEmail.trim(),
    dateFormat: DEFAULT_DATE_FORMAT,
    fields: DEFAULT_CANCEL_REQUEST_FIELDS.map((id) => ({ id, required: true })),
  };
}

function parseField(raw: unknown): ReservationFieldSetting | null {
  const item = (typeof raw === "string" ? { id: raw } : raw) as Record<string, unknown> | null;
  if (!item || typeof item !== "object") return null;
  const id = String(item.id ?? "").trim();
  const required = typeof item.required === "boolean" ? item.required : true;
  if (isCancelRequestField(id)) return { id, required };
  if (!id || typeof raw === "string" || (RESERVATION_FIELDS as readonly string[]).includes(id)) return null;
  return { id, label: String(item.label ?? ""), type: "text", required, custom: true };
}

/** The stored ``content.cancelRequest`` (object or JSON string) for editing, or ``null`` when there is none. */
export function parseCancelRequest(raw: unknown): CancelRequest | null {
  let value = raw;
  if (typeof value === "string") {
    try {
      value = JSON.parse(value);
    } catch {
      return null;
    }
  }
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const item = value as Record<string, unknown>;
  const fields = (Array.isArray(item.fields) ? item.fields : [])
    .map(parseField)
    .filter((field): field is ReservationFieldSetting => field !== null)
    .filter((field, index, all) => all.findIndex((other) => other.id === field.id) === index);
  const dateFormat = String(item.dateFormat ?? item.date_format ?? "");
  return {
    enabled: item.enabled !== false,
    mode: item.mode === "link_out" ? "link_out" : "enquiry",
    urlTemplate: String(item.urlTemplate ?? item.url_template ?? ""),
    enquiryEmail: String(item.enquiryEmail ?? item.enquiry_email ?? ""),
    dateFormat: DATE_FORMATS.includes(dateFormat) ? dateFormat : DEFAULT_DATE_FORMAT,
    fields: fields.length ? fields : DEFAULT_CANCEL_REQUEST_FIELDS.map((id) => ({ id, required: true })),
  };
}

/** Whether a Cancel Booking record has the cancel request flow switched on. */
export function cancelRequestOn(record: { content?: Record<string, unknown> } | null | undefined): boolean {
  return parseCancelRequest(record?.content?.cancelRequest)?.enabled === true;
}

/** Id for a custom detail named *label*: kept when it was not made from the old label, else a unique slug. */
export function customFieldId(field: ReservationFieldSetting, label: string, otherIds: string[]): string {
  const oldBase = slugify(field.label || "");
  const madeFromLabel = !field.id || field.id === oldBase || (!!oldBase && new RegExp(`^${oldBase}_\\d+$`).test(field.id));
  if (!madeFromLabel && SLUG_RE.test(field.id) && !otherIds.includes(field.id)) return field.id;
  const base = slugify(label);
  if (!base || isCancelRequestField(base)) return base;
  const taken = [...TAKEN_IDS, ...otherIds];
  let id = base;
  for (let n = 2; taken.includes(id); n++) id = `${base}_${n}`;
  return id;
}

/** Placeholders a cancel page link can use: the chosen details. */
export const cancelRequestPlaceholders = (setting: CancelRequest) => setting.fields.map((field) => field.id).filter(Boolean);

/** Whether the chatbot also asks for an email, because neither email nor phone is chosen for an email request. */
export function addsEmail(setting: CancelRequest): boolean {
  return setting.mode === "enquiry" && !setting.fields.some((field) => !field.custom && CONTACT_FIELDS.includes(field.id));
}

function customFieldsError(setting: CancelRequest): string | undefined {
  const custom = cancelRequestCustomFields(setting);
  if (custom.length > MAX_CUSTOM_FIELDS) return `Add at most ${MAX_CUSTOM_FIELDS} extra details.`;
  const ids = setting.fields.map((field) => field.id);
  for (const field of custom) {
    const label = (field.label || "").trim();
    if (!label) return "Every extra detail needs a name.";
    if (label.length > MAX_FIELD_LABEL) return `"${label.slice(0, 20)}…" is too long (at most ${MAX_FIELD_LABEL} characters).`;
    if (isCancelRequestField(field.id)) return `"${label}" is already a standard detail; tick it instead.`;
    if (!SLUG_RE.test(field.id)) return `Use letters or numbers in "${label}".`;
    if (TAKEN_IDS.includes(field.id)) return `"${label}" can't be used as a name; pick another.`;
    if (ids.indexOf(field.id) !== ids.lastIndexOf(field.id)) return `"${label}" is used more than once.`;
  }
  return undefined;
}

function urlError(setting: CancelRequest): string | undefined {
  const template = setting.urlTemplate.trim();
  if (!template) return "Cancel page link is required!";
  if (!/^https?:\/\/\S+$/i.test(template)) return "Enter a link that starts with http:// or https:// and has no spaces.";
  if (template.length > MAX_URL_TEMPLATE) return `Keep the link to ${MAX_URL_TEMPLATE} characters.`;
  const chosen = cancelRequestPlaceholders(setting);
  const unknown = templatePlaceholders(template).filter((name) => !chosen.includes(name));
  if (unknown.length) return `The link uses {${unknown[0]}}, which isn't one of the details you ask for.`;
  return undefined;
}

function emailError(setting: CancelRequest): string | undefined {
  const email = setting.enquiryEmail.trim();
  if (!email) return "Hotel email is required!";
  if (!EMAIL_RE.test(email) || email.length > MAX_EMAIL) return "Enter a valid email address!";
  return undefined;
}

/**
 * Errors the admin API would reject the setting with, keyed by input. A
 * switched-off setting needn't have a link or email yet; what it has is
 * saved only when valid (see ``cancelRequestPayload``).
 */
export function cancelRequestErrors(setting: CancelRequest | null | undefined): CancelRequestErrors {
  if (!setting?.enabled) return {};
  const errors: CancelRequestErrors = {};
  const fields = customFieldsError(setting);
  if (fields) errors.cancelRequestFields = fields;
  if (setting.mode === "link_out") {
    const url = urlError(setting);
    if (url) errors.cancelRequestUrl = url;
  } else {
    const email = emailError(setting);
    if (email) errors.cancelRequestEmail = email;
  }
  return errors;
}

export interface CancelRequestPayload {
  enabled: boolean;
  mode: ReservationMode;
  urlTemplate?: string;
  enquiryEmail?: string;
  dateFormat: string;
  fields: { id: string; label?: string; type?: string; required: boolean }[];
}

/** ``content.cancelRequest`` as the admin API saves it: built-in details first, then the extra ones. */
export function cancelRequestPayload(setting: CancelRequest): CancelRequestPayload {
  const builtins = CANCEL_REQUEST_FIELDS.flatMap((id) => setting.fields.filter((field) => !field.custom && field.id === id));
  const custom = setting.enabled || !customFieldsError(setting) ? cancelRequestCustomFields(setting) : [];
  const fields = [...builtins, ...custom].map((field) =>
    field.custom
      ? { id: field.id, label: (field.label || "").trim(), type: "text", required: field.required }
      : { id: field.id, required: field.required });
  const base: CancelRequestPayload = {
    enabled: setting.enabled,
    mode: setting.mode,
    dateFormat: DATE_FORMATS.includes(setting.dateFormat) ? setting.dateFormat : DEFAULT_DATE_FORMAT,
    fields,
  };
  if (setting.mode === "link_out") {
    const keep = setting.enabled || (setting.urlTemplate.trim() && !urlError({ ...setting, fields: [...builtins, ...custom] }));
    return keep ? { ...base, urlTemplate: setting.urlTemplate.trim() } : base;
  }
  const keep = setting.enabled || (setting.enquiryEmail.trim() && !emailError(setting));
  return keep ? { ...base, enquiryEmail: setting.enquiryEmail.trim() } : base;
}
